import React, { useEffect, useState } from "react";
import {
  getPendingVerificationOrganizations,
  getPendingVerificationUsers,
  getPublicSurvey,
  setOrganizationVerificationStatus,
  setUserVerificationStatus,
} from "../../api/api";
import {
  NotificationTabHeader,
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationRequestCard,
} from "../notifications_page/NotificationTabShared";
import { Button } from "../ui/button";
import SurveyAnswersReadOnly from "../survey/SurveyAnswersReadOnly";
import { SOLO_LEADER_SURVEY_ID } from "../survey/surveyUtils";

/**
 * Pending verification queue for platform administrators (orgs and individuals).
 * @param {{adminId: number}} props
 * @returns {JSX.Element}
 */
function PendingVerificationTab({ adminId }) {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [surveyDefinition, setSurveyDefinition] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workingId, setWorkingId] = useState("");

  useEffect(() => {
    let mounted = true;
    async function loadPending() {
      setLoading(true);
      setError("");
      const [orgResult, userResult, surveyResult] = await Promise.all([
        getPendingVerificationOrganizations(adminId),
        getPendingVerificationUsers(adminId),
        getPublicSurvey(SOLO_LEADER_SURVEY_ID),
      ]);
      if (!mounted) return;
      if (orgResult.error) {
        setError(orgResult.error);
      } else {
        setOrgs(orgResult.data || []);
      }
      if (userResult.error) {
        setError((prev) => prev || userResult.error);
      } else {
        setUsers(userResult.data || []);
      }
      if (!surveyResult.error && surveyResult.data?.definition) {
        setSurveyDefinition(surveyResult.data.definition);
      }
      setLoading(false);
    }
    if (adminId) {
      loadPending();
    }
    return () => {
      mounted = false;
    };
  }, [adminId]);

  /**
   * @param {number} orgId
   * @param {"approve"|"reject"} decision
   * @returns {Promise<void>}
   */
  async function onOrgDecision(orgId, decision) {
    setWorkingId(`org-${orgId}`);
    const result = await setOrganizationVerificationStatus(adminId, orgId, decision);
    setWorkingId("");
    if (result.error) {
      setError(result.error);
      return;
    }
    setOrgs((prev) => prev.filter((item) => item.id !== orgId));
  }

  /**
   * @param {number} userId
   * @param {"approve"|"reject"} decision
   * @returns {Promise<void>}
   */
  async function onUserDecision(userId, decision) {
    setWorkingId(`user-${userId}`);
    const result = await setUserVerificationStatus(adminId, userId, decision);
    setWorkingId("");
    if (result.error) {
      setError(result.error);
      return;
    }
    setUsers((prev) => prev.filter((item) => item.id !== userId));
  }

  const empty = !loading && orgs.length === 0 && users.length === 0;

  return (
    <div>
      <NotificationTabHeader
        title="Pending verification"
        description="Review and approve or reject organizations and individual ministry leaders waiting for platform verification."
      />

      {error ? <NotificationErrorPanel message={error} /> : null}

      {loading ? (
        <NotificationEmptyState title="Loading pending accounts..." className="border-solid bg-ui-card" />
      ) : null}

      {empty ? (
        <NotificationEmptyState
          title="No pending accounts"
          subtitle="All pending verification requests have been processed."
          className="border-solid bg-ui-card"
        />
      ) : null}

      {!loading && users.length > 0 ? (
        <div className="mb-8 space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
            Individuals
          </h3>
          {users.map((person) => {
            const fullName = `${person.first_name || ""} ${person.last_name || ""}`.trim();
            const expanded = expandedUserId === person.id;
            return (
              <NotificationRequestCard key={`user-${person.id}`}>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-semibold text-text-primary">
                        {fullName || person.username}
                      </h3>
                      <p className="truncate text-sm text-text-secondary">@{person.username}</p>
                      <p className="truncate text-sm text-text-muted">{person.email}</p>
                      {person.location ? (
                        <p className="truncate text-sm text-text-muted">{person.location}</p>
                      ) : null}
                      {person.denomination ? (
                        <p className="truncate text-sm text-text-muted">{person.denomination}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setExpandedUserId(expanded ? 0 : person.id)}
                      >
                        {expanded ? "Hide application" : "View application"}
                      </Button>
                      <Button
                        onClick={() => onUserDecision(person.id, "approve")}
                        disabled={workingId === `user-${person.id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => onUserDecision(person.id, "reject")}
                        disabled={workingId === `user-${person.id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                  {expanded && surveyDefinition ? (
                    <div className="border-t border-ui-border pt-4">
                      <SurveyAnswersReadOnly
                        definition={surveyDefinition}
                        answers={person.verification_answers || {}}
                      />
                    </div>
                  ) : null}
                </div>
              </NotificationRequestCard>
            );
          })}
        </div>
      ) : null}

      {!loading && orgs.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
            Organizations
          </h3>
          {orgs.map((org) => (
            <NotificationRequestCard key={`org-${org.id}`}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-semibold text-text-primary">{org.name}</h3>
                  <p className="truncate text-sm text-text-secondary">@{org.username}</p>
                  <p className="truncate text-sm text-text-muted">{org.email}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    onClick={() => onOrgDecision(org.id, "approve")}
                    disabled={workingId === `org-${org.id}`}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onOrgDecision(org.id, "reject")}
                    disabled={workingId === `org-${org.id}`}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </NotificationRequestCard>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default PendingVerificationTab;
